/**
 * Block 13 PR-G Phase 2 Step 6 — share contract PDF через Web Share API
 * + deeplink fallback к мессенджерам (WhatsApp/Telegram/MAX/Gmail/Mail).
 *
 * Mirror Block 12 shareQuotePdf.ts pattern (PR-D-B Phase G4). Hybrid behaviour:
 *
 *   - **Mobile (channel='auto')**: native `navigator.share({files: [pdf]})`
 *     при capability + permissions. Falls back to download когда canShare === false.
 *
 *   - **Desktop (channel='whatsapp' | 'telegram' | 'max' | 'gmail' | 'mail')**:
 *     1. Open URL synchronously (preserves transient userActivation для popup
 *        blocker bypass) — ДО async render.
 *     2. Render PDF + trigger download.
 *     3. Show toast «PDF загружен, прикрепите его к сообщению».
 *
 * **Locale-aware share text** (ru/kk/uz) per template.meta.lang — clients
 * speaking respective language get appropriate message.
 *
 * Reference:
 *   - apps/web/src/features/calculator/quote/services/shareQuotePdf.ts (pattern)
 *   - LESSONS_LEARNED 2026-05-18 #4 (Web Share API for files, deeplinks for text)
 *   - Block_13_PR_G_phase_0_planning.md Phase 2 Step 6
 */

import type { ContractDraft, ContractTemplate } from '@pomoshnik/shared';

import { buildContractFilename, triggerContractPdfDownload } from './generateContractPdf';
import { renderContractToPdf } from './renderContractToPdf';

// ── Public types ───────────────────────────────────────────────────────────

export type ContractShareChannel = 'auto' | 'whatsapp' | 'telegram' | 'max' | 'mail' | 'gmail';

export type DesktopShareChannel = Exclude<ContractShareChannel, 'auto'>;

export type ContractShareResult =
  | { status: 'shared'; method: 'native' }
  | { status: 'cancelled' }
  | { status: 'downloaded'; method: 'fallback' }
  | { status: 'desktop-direct'; channel: DesktopShareChannel }
  | { status: 'error'; error: unknown };

export interface ShareContractOptions {
  channel?: ContractShareChannel;
  /** DI seam для тестов — defaults to global `navigator`. */
  nav?: Pick<Navigator, 'canShare' | 'share'>;
  /** Override download trigger — defaults to triggerContractPdfDownload. */
  download?: (blob: Blob, filename: string) => void;
  /** Override URL opener (window.open / window.location). */
  openUrl?: (url: string, target: '_self' | '_blank') => void;
}

// ── Share text composition ─────────────────────────────────────────────────

/**
 * Locale-aware share text. Используется как:
 *   - text для navigator.share + pre-fill в WhatsApp/Telegram deeplinks
 *
 * **Phase 6.5E Item E (Aibek decision 2026-05-24):** simplified text — без
 * contract №, без даты, без «PDF отдельным сообщением». Single short sentence
 * на каждый из 3 локалей. Aibek explicit: «Здравствуйте, отправляю Вам
 * договор на изготовление мебели». Kk/uz варианты переведены consistently.
 *
 * `draft` parameter preserved для signature stability (используется callers
 * через типизированный wire-up; не дёргается внутри после simplification).
 */
export function buildContractShareText(_draft: ContractDraft, template: ContractTemplate): string {
  switch (template.meta.lang) {
    case 'ru':
      return 'Здравствуйте, отправляю Вам договор на изготовление мебели';
    case 'kk':
      return 'Сәлеметсіз бе, Сізге жиһаз жасау бойынша шартты жіберіп отырмын';
    case 'uz':
      return 'Assalomu alaykum, sizga mebel tayyorlash boʻyicha shartnomani yuboryapman';
  }
}

// ── Desktop deeplink URL builders ──────────────────────────────────────────

function buildWhatsAppUrl(text: string): string {
  return `https://wa.me/?text=${encodeURIComponent(text)}`;
}

/** Telegram web — pre-fill через URL ломает preview (Block 12 G4 finding). */
const TELEGRAM_WEB_URL = 'https://web.telegram.org/';

const MAX_WEB_URL = 'https://web.max.ru/';

function buildMailtoUrl(title: string, body: string): string {
  return `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(body)}`;
}

function buildGmailComposeUrl(title: string, body: string): string {
  return `https://mail.google.com/mail/?view=cm&fs=1&su=${encodeURIComponent(title)}&body=${encodeURIComponent(body)}`;
}

function defaultOpenUrl(url: string, target: '_self' | '_blank'): void {
  if (typeof window === 'undefined') return;
  if (target === '_self') {
    window.location.href = url;
    return;
  }
  // Use the target parameter (not literal '_blank') чтобы будущие target values
  // не silently coercились. Per reviewer feedback Phase 2.
  window.open(url, target, 'noopener,noreferrer');
}

// ── Capability detection ───────────────────────────────────────────────────

interface ShareCanShareNav {
  canShare?: Navigator['canShare'];
  share?: Navigator['share'];
}

/**
 * `canShare({files})` может throw TypeError на iOS Safari 14 WebKit вместо
 * return false. Wrap в try/catch.
 */
function canShareFiles(nav: ShareCanShareNav | undefined, data: ShareData): boolean {
  if (!nav || typeof nav.canShare !== 'function' || typeof nav.share !== 'function') {
    return false;
  }
  try {
    return nav.canShare(data);
  } catch {
    return false;
  }
}

function isCancelledError(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  return err.name === 'AbortError' || err.name === 'NotAllowedError';
}

// ── Toast text per desktop channel ─────────────────────────────────────────

export function buildContractDesktopShareToast(channel: DesktopShareChannel): string {
  switch (channel) {
    case 'whatsapp':
      return 'WhatsApp открыт в новой вкладке. Прикрепите PDF из Downloads, текст уже в сообщении.';
    case 'telegram':
      return 'Telegram открыт в новой вкладке. Выберите чат, прикрепите PDF из Downloads, вставьте текст (Ctrl+V).';
    case 'max':
      return 'MAX открыт в новой вкладке. Откройте чат, прикрепите PDF из Downloads, вставьте текст (Ctrl+V).';
    case 'mail':
      return 'Откройте почтовый клиент и прикрепите PDF из Downloads.';
    case 'gmail':
      return 'Gmail открыт в новой вкладке. Прикрепите PDF из Downloads.';
  }
}

// ── Main share entry point ─────────────────────────────────────────────────

export async function shareContractPdf(
  draft: ContractDraft,
  template: ContractTemplate,
  options: ShareContractOptions = {},
): Promise<ContractShareResult> {
  const {
    channel = 'auto',
    download = triggerContractPdfDownload,
    openUrl = defaultOpenUrl,
  } = options;
  const nav = options.nav ?? (typeof navigator !== 'undefined' ? navigator : undefined);

  try {
    const shareText = buildContractShareText(draft, template);
    const title = `Договор №${draft.contractNum || '___'}`;

    if (channel !== 'auto') {
      // ===== Desktop direct URL path — skip navigator.share entirely =====
      // 1. Sync openUrl FIRST (preserves userActivation для popup bypass).
      if (channel === 'whatsapp') {
        openUrl(buildWhatsAppUrl(shareText), '_blank');
      } else if (channel === 'telegram') {
        openUrl(TELEGRAM_WEB_URL, '_blank');
      } else if (channel === 'max') {
        openUrl(MAX_WEB_URL, '_blank');
      } else if (channel === 'gmail') {
        openUrl(buildGmailComposeUrl(title, shareText), '_blank');
      } else if (channel === 'mail') {
        openUrl(buildMailtoUrl(title, shareText), '_self');
      }

      // 2. Async render + auto-download (на ВСЕХ desktop channels).
      const blob = await renderContractToPdf(draft, template);
      const filename = buildContractFilename(draft);
      download(blob, filename);

      return { status: 'desktop-direct', channel };
    }

    // ===== Mobile native path (channel === 'auto') =====
    const blob = await renderContractToPdf(draft, template);
    const filename = buildContractFilename(draft);
    const file = new File([blob], filename, { type: 'application/pdf' });
    const shareData: ShareData = { files: [file], title, text: shareText };

    const canShare = canShareFiles(nav, shareData);

    if (canShare && nav !== undefined) {
      try {
        await nav.share!(shareData);
        return { status: 'shared', method: 'native' };
      } catch (err) {
        if (isCancelledError(err)) return { status: 'cancelled' };
        return { status: 'error', error: err };
      }
    }

    download(blob, filename);
    return { status: 'downloaded', method: 'fallback' };
  } catch (err) {
    return { status: 'error', error: err };
  }
}
